import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, Stat, Drawer, ConfirmDialog } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { adminApi } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';
import { useMonitorStream } from '@/shared/hooks/useMonitorStream';
import { useTicker, elapsedSec, fmtDuration } from '@/shared/hooks/useTicker';
import { TeamCallbacksPanel } from '../components/TeamCallbacksPanel';
import { useAuthStore } from '@/shared/stores/auth';
import { useActiveSnoop } from '@/shared/stores/activeSnoop';
import { usePageBadge } from '@/shared/stores/pageBadge';
import { FormSelect } from '@/components/ui';
import { Modal } from '@/components/ui';
import { groupTargetsBySupervisor } from '../../agent/components/CallControlBar';
import { labelFor as roleLabel } from '@/shared/lib/labels';
import type { TransferTarget } from '@/shared/api/agentApi';
import { AgentPerformance } from './live/AgentPerformance';
import { type LiveActions } from './live/AgentActions';
import { WallboardTiles, StatusTiles, QueueCards } from './live/LiveTiles';

interface AgentRow {
  id: number;
  name: string;
  role: string;
  extension?: string;
  status: string;
  online?: boolean;
  presence_since?: string | null;
  on_call: boolean;
  call_id?: number | null;
  channel_id?: string;
  call_started_at?: string | null;
}
interface QueueRow {
  id: number;
  name: string;
  ext?: string;
  sla_sec: number;
  calls_today: number;
  answered_today: number;
  sla_pct: number | null;
  waiting_now: number;
}
interface Wallboard {
  waiting_now: number;
  longest_wait_sec: number;
  avg_wait_sec: number;
  agents_available: number;
  agents_oncall: number;
}
interface MonitorData {
  agents: AgentRow[];
  live_agents?: AgentRow[];
  totals?: Record<string, number>;
  queues?: QueueRow[];
  wallboard?: Wallboard;
  /** The requester's live snoop channel id, or null when the backend has none. */
  my_active_snoop?: string | null;
}
interface AgentDetail { calls_today: number; answered_today: number; missed_today: number; avg_aht_sec: number; qa_avg: number | null; on_call: boolean; current_call_started_at: string | null }

export function LiveMonitor() {
  // Reuse the shared SSE snapshot; live_agents is the canonical agent list.
  const { data, connected } = useMonitorStream<MonitorData>('/admin/live', 2000);
  const now = useTicker(1000);
  const [detail, setDetail] = useState<{ agent: AgentRow; info: AgentDetail | null } | null>(null);
  const meId = useAuthStore((s) => s.user?.id);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Takeover confirm target.
  const [takeoverTarget, setTakeoverTarget] = useState<AgentRow | null>(null);
  const [transferFor, setTransferFor] = useState<AgentRow | null>(null);
  // Agent to highlight/listen to, from a keyword-alert deep link (?listen_agent=).
  const listenAgentId = searchParams.get('listen_agent');
  const cardRefs = useRef<Record<number, HTMLDivElement | null>>({});
  // Fire the auto-listen at most once per deep-link arrival.
  const autoListenedFor = useRef<string | null>(null);

  const agents = data?.live_agents ?? data?.agents ?? [];

  // מפרסם את מחוון החיבור ("חי"/"מתעדכן") אל ה-Topbar, ליד כותרת הדף.
  // מנקה ביציאה מהמסך כדי שהתג לא ידבק למסכים אחרים.
  useEffect(() => {
    usePageBadge.getState().setBadge(
      connected ? { label: 'חי', tone: 'ok', dot: true } : { label: 'מתעדכן', tone: 'neutral', dot: true }
    );
    return () => usePageBadge.getState().setBadge(null);
  }, [connected]);

  useEffect(() => {
    if (!listenAgentId) return;
    const id = Number(listenAgentId);
    const target = agents.find((a) => a.id === id);
    if (!target) return; // agent not in the live snapshot yet — wait for the next tick
    cardRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (autoListenedFor.current !== listenAgentId && target.on_call && target.channel_id) {
      autoListenedFor.current = listenAgentId;
      void snoop(target.channel_id, 'listen', target.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listenAgentId, agents]);

  // סוגר את סרגל ההאזנה הצף כשהבקאנד כבר לא מחזיק snoop פעיל למנהל, או כשהשיחה
  // שהמנהל האזין לה נגמרה (הנציג כבר לא on_call). מקורות סגירה:
  //   1. my_active_snoop=null בסנאפשוט → הבקאנד פירק את ה-snoop (teardown /
  //      originate כושל של שלוחת המנהל = "מאזין בלי אודיו") — סמכותי.
  //   2. הנציג ירד מ-on_call → השיחה נגמרה.
  // האודיו כבר נותק בבקאנד; זה רק מנקה את ה-UI ("סרגל רפאים"). תנאי ה-data מגן
  // מפני ניקוי כוזב לפני snapshot ראשון.
  useEffect(() => {
    const active = useActiveSnoop.getState().active;
    if (!active?.agentId || !data) return;
    // מחכים לפחות ל-snapshot אחד אחרי שההאזנה התחילה — כדי לא לסגור לפני
    // ש-my_active_snoop הספיק להתאכלס בבקאנד.
    const grace = Date.now() - active.startedAt > 3000;
    const backendDropped = grace && ('my_active_snoop' in data) && data.my_active_snoop == null;
    const still = agents.find((a) => a.id === active.agentId);
    const callEnded = !still || !still.on_call;
    if (backendDropped || callEnded) {
      useActiveSnoop.getState().clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents, data]);

  async function snoop(channelId: string, mode: 'listen' | 'whisper' | 'barge', agentId?: number): Promise<number | null> {
    try {
      // אם המנהל כבר מאזין לשיחה אחרת — מורידים אותה קודם כדי לא להשאיר רגל
      // snoop יתומה ולא לשמוע שתי שיחות מעורבבות (מעבר בין נציגים בלי "רד").
      const prev = useActiveSnoop.getState().active;
      if (prev && prev.channelId !== channelId) {
        try { await adminApi.stopSnoop(prev.snoopChannelId); } catch { /* best-effort */ }
        useActiveSnoop.getState().clear();
      }

      const res = await adminApi.snoop(channelId, mode);
      // Honor the ARI result instead of blind success.
      if (res?.success) {
        toast(`${labelMode(mode)} פעיל — השלוחה שלך מצלצלת`, 'ok');
        // מפעיל את סרגל השליטה הצף: "אתה מאזין ל...", עם החלפת מצב/ירידה מכל מסך.
        const agentName = agents.find((a) => a.id === agentId)?.name ?? 'נציג';
        useActiveSnoop.getState().start({
          channelId, snoopChannelId: res?.data?.snoop_channel_id ?? null,
          mode, agentId: agentId ?? 0, agentName,
          startedAt: Date.now(),
        });
        return null;
      }
      toast(res?.message ?? 'הפעולה נכשלה ב-Asterisk', 'danger');
      return null;
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה', 'danger');
      return null;
    }
  }

  async function takeover(a: AgentRow) {
    if (a.call_id == null) return;
    try {
      const res = await adminApi.takeoverCall(a.call_id);
      toast(res?.message ?? 'השיחה נחטפה אליך', 'ok');
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה בחטיפת השיחה', 'danger');
    } finally {
      setTakeoverTarget(null);
    }
  }

  // Shared action handlers, reused by the live cards AND the performance
  // section (so both surfaces drive the same live call).
  const liveActions: LiveActions = {
    snoop,
    onTransfer: (agent) => setTransferFor(agent as AgentRow),
    onTakeover: (agent) => setTakeoverTarget(agent as AgentRow),
  };

  async function openAgent(id: number, name: string) {
    const stub = { id, name } as AgentRow;
    setDetail({ agent: stub, info: null });
    try {
      const info = (await adminApi.agentLive(id)) as AgentDetail;
      setDetail({ agent: stub, info });
    } catch {
      setDetail({ agent: stub, info: null });
    }
  }

  if (!data) return <Card>טוען…</Card>;

  const totals = data.totals ?? {};
  const wb = data.wallboard;
  const queues = data.queues ?? [];

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      {/* הכותרת "ניטור חי" ותג ה-"חי" מוצגים ב-Topbar (כותרת הדף הדינמית +
          usePageBadge), כדי לא לשכפל כותרת בגוף הדף. */}

      {/* Agent performance funnel + table/cards — the first thing on the screen.
          Cross-references the live snapshot so on-call agents get the same
          listen/whisper/barge/transfer/takeover actions; the agent name
          opens the drill-down drawer, and a ?listen_agent= deep link highlights
          + scrolls to + auto-listens on that agent's row/card. */}
      {/* מחוון עדכון חי (בעיצוב: "מתעדכן כל שנייה" בקצה השמאלי) */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--muted)', marginBottom: -4 }}>
        {connected
          ? <><span className="live-dot" style={{ width: 7, height: 7 }} /> מתעדכן בזמן אמת</>
          : <><span style={{ width: 7, height: 7, borderRadius: 'var(--r-pill)', background: 'var(--faint)' }} /> מתעדכן כל 2 שניות</>}
      </div>

      <AgentPerformance
        liveAgents={agents}
        actions={liveActions}
        onOpenAgent={openAgent}
        highlightAgentId={listenAgentId != null ? Number(listenAgentId) : null}
        registerRef={(id, el) => { cardRefs.current[id] = el as HTMLDivElement | null; }}
      />

      {/* לוח המתנה: ממתינים כעת + זמינות נציגים */}
      {wb && <WallboardTiles wb={wb} />}

      {/* ספירת נציגים לפי סטטוס נוכחות */}
      <StatusTiles totals={totals} labelFor={labelFor} />

      {/* Pending team follow-ups: reassign / take over */}
      <TeamCallbacksPanel meId={meId} />

      {/* Queues live: waiting + SLA per queue */}
      {queues.length > 0 && <QueueCards queues={queues} />}

      <Drawer isOpen={!!detail} onClose={() => setDetail(null)} title={detail ? `${detail.agent.name} — ביצועי היום` : ''}>
        {detail && (!detail.info ? (
          <div style={{ color: 'var(--muted)' }}>טוען…</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Stat label="שיחות היום" value={detail.info.calls_today} />
            <Stat label="נענו" value={detail.info.answered_today} />
            <Stat label="הוחמצו" value={detail.info.missed_today} />
            <Stat label="זמן טיפול ממוצע" value={fmtDuration(detail.info.avg_aht_sec)} />
            <Stat label="ציון QA (30 ימים)" value={detail.info.qa_avg != null ? `${detail.info.qa_avg}` : '—'} />
            {detail.info.on_call && (
              <Stat label="בשיחה כעת" value={fmtDuration(elapsedSec(detail.info.current_call_started_at, now))} />
            )}
          </div>
        ))}
      </Drawer>

      <ConfirmDialog
        isOpen={!!takeoverTarget}
        onClose={() => setTakeoverTarget(null)}
        onConfirm={() => { if (takeoverTarget) void takeover(takeoverTarget); }}
        title="חטיפת שיחה"
        message={takeoverTarget ? `לחטוף את השיחה של ${takeoverTarget.name} אליך? הנציג ינותק והשיחה תעבור לשלוחה שלך.` : undefined}
        confirmLabel="חטוף"
      />

      {transferFor?.call_id != null && (
        <LiveTransferModal
          agent={transferFor}
          onClose={() => setTransferFor(null)}
        />
      )}
    </div>
  );
}

/** Supervisor-side "transfer this agent's call to another agent" picker. */
export function LiveTransferModal({ agent, onClose }: { agent: { id: number; name: string; call_id?: number | null }; onClose: () => void }) {
  const [targets, setTargets] = useState<TransferTarget[]>([]);
  const [userId, setUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void adminApi.liveTransferTargets(agent.id)
      .then(setTargets)
      .catch((e) => toast(errMsg(e, 'שגיאה בטעינת נציגים'), 'danger'))
      .finally(() => setLoading(false));
  }, [agent.id]);

  const groups = groupTargetsBySupervisor(targets);

  async function submit() {
    if (agent.call_id == null || !userId) return;
    setBusy(true);
    try {
      const res = await adminApi.transferLiveCall(agent.call_id, Number(userId));
      toast(res?.message ?? 'השיחה הועברה', 'ok');
      onClose();
    } catch (e) {
      toast(errMsg(e, 'שגיאה בהעברת השיחה'), 'danger');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`העברת השיחה של ${agent.name}`}
      size="sm"
      footer={<>
        <button type="button" className="btn" onClick={onClose} disabled={busy}>ביטול</button>
        <button type="button" className="btn primary" onClick={submit} disabled={busy || !userId}>{busy ? '…' : 'העבר'}</button>
      </>}
    >
      {loading ? (
        <div style={{ color: 'var(--muted)' }}>טוען…</div>
      ) : targets.length === 0 ? (
        <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין נציגים זמינים כרגע להעברה.</div>
      ) : (
        <FormSelect value={userId} onChange={(e) => setUserId(e.target.value)} aria-label="בחר נציג">
          <option value="">בחר נציג…</option>
          {groups.map((g) => (
            <optgroup key={g.key} label={g.label}>
              {g.items.map((t) => (
                <option key={t.id} value={t.id}>{t.name} · {roleLabel('role', t.role)}</option>
              ))}
            </optgroup>
          ))}
        </FormSelect>
      )}
    </Modal>
  );
}

function errMsg(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;
}

function labelFor(s: string) {
  // connected = דפדפן חי עכשיו; offline = סטטוס "לא זמין" (נבחר או אחרי ניתוק).
  return ({ connected: 'מחוברים עכשיו', available: 'זמין', oncall: 'בשיחה', ringing: 'מצלצל', acw: 'אחרי שיחה', break: 'הפסקה', lunch: 'ארוחת צהריים', paperwork: 'עבודת ניירת', training: 'הכשרה', offline: 'לא זמין' } as Record<string, string>)[s] ?? s;
}
function labelMode(m: string) {
  return ({ listen: 'האזנה', whisper: 'לחישה', barge: 'הצטרף לשיחה' } as Record<string, string>)[m] ?? m;
}
